import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { BoundingBox, SharedGeometries } from '../types';

const defaultCubeBounds: BoundingBox = {
  min: [-0.5, -0.5, -0.5],
  max: [0.5, 0.5, 0.5],
};

const createCuboidLines = () => {
  const geometry = new THREE.BufferGeometry();
  const indices = new Uint16Array([
    0, 1, 1, 2, 2, 3, 3, 0, 4, 5, 5, 6, 6, 7, 7, 4, 0, 4, 1, 5, 2, 6, 3, 7,
  ]);
  const positions = new Float32Array(8 * 3);

  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const { min, max } = defaultCubeBounds;
  /*
        5____4
      1/___0/|
      | 6__|_7
      2/___3/
      0: max.x, max.y, max.z
      1: min.x, max.y, max.z
      2: min.x, min.y, max.z
      3: max.x, min.y, max.z
      4: max.x, max.y, min.z
      5: min.x, max.y, min.z
      6: min.x, min.y, min.z
      7: max.x, min.y, min.z
  */
  const [x0, y0, z0] = min;
  const [x1, y1, z1] = max;

  const buffer = geometry.attributes.position;
  buffer.setXYZ(0, x1, y1, z1);
  buffer.setXYZ(1, x0, y1, z1);
  buffer.setXYZ(2, x0, y0, z1);
  buffer.setXYZ(3, x1, y0, z1);
  buffer.setXYZ(4, x1, y1, z0);
  buffer.setXYZ(5, x0, y1, z0);
  buffer.setXYZ(6, x0, y0, z0);
  buffer.setXYZ(7, x1, y0, z0);
  geometry.attributes.position.needsUpdate = true;

  return geometry;
};

const createAnchorLine = () => {
  const geometry = new THREE.BoxGeometry();
  geometry.scale(0.002, 0.002, 1);
  geometry.translate(0, 0, -0.5);
  return geometry;
};

const createAnchorPlane = () => {
  const geometry = new THREE.PlaneGeometry(1, 1);
  geometry.scale(2, 2, 1);
  geometry.translate(0, 0, -1);
  return geometry;
};

const createPictureFrameLines = () => {
  const geometry = new THREE.BufferGeometry();
  const indices = new Uint16Array([0, 1, 1, 2, 2, 3, 3, 0]);
  const positions = new Float32Array(4 * 3);

  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const { min, max } = defaultCubeBounds;
  /*
      1___0
      |   |
      2___3
      0: max.x, max.y, 0
      1: min.x, max.y, 0
      2: min.x, min.y, 0
      3: max.x, min.y, 0
  */
  const [x0, y0] = min;
  const [x1, y1] = max;

  const buffer = geometry.attributes.position;
  buffer.setXYZ(0, x1, y1, 0);
  buffer.setXYZ(1, x0, y1, 0);
  buffer.setXYZ(2, x0, y0, 0);
  buffer.setXYZ(3, x1, y0, 0);
  geometry.attributes.position.needsUpdate = true;

  return geometry;
};

const createHoveredPictureFrameLines = () => {
  const geometry = new THREE.BufferGeometry();
  const indices = new Uint16Array([0, 1]);
  const positions = new Float32Array(2 * 3);

  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const buffer = geometry.attributes.position;
  buffer.setXYZ(0, 0, 0, 0);
  buffer.setXYZ(1, 0, 0, -1);
  geometry.attributes.position.needsUpdate = true;

  return geometry;
};

const createPlane = () => {
  const geometry = new THREE.PlaneGeometry(1, 1);
  return geometry;
};

export const useSharedGeometries = () => {
  const sharedGeometries = useRef<SharedGeometries>({
    viewerSphere: new THREE.SphereGeometry(1, 256, 128),
    anchorSphere: new THREE.SphereGeometry(1, 32, 16),
    boxGeometry: new THREE.BoxGeometry(),
    cuboidLines: createCuboidLines(),
    anchorLine: createAnchorLine(),
    anchorPlane: createAnchorPlane(),
    pictureFrameLines: createPictureFrameLines(),
    hoveredPictureFrameLines: createHoveredPictureFrameLines(),
    plane: createPlane(),
  });

  useEffect(() => {
    return () => {
      const { current } = sharedGeometries;
      current.viewerSphere.dispose();
      current.anchorSphere.dispose();
      current.boxGeometry.dispose();
      current.cuboidLines.dispose();
      current.anchorLine.dispose();
      current.anchorPlane.dispose();
      current.pictureFrameLines.dispose();
      current.hoveredPictureFrameLines.dispose();
      current.plane.dispose();
    };
  }, []);

  return sharedGeometries.current;
};
